/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useMemo } from 'react';
import * as THREE from 'three';
import { useGLTF } from '@react-three/drei';

interface ThreeDModelProps {
  url: string;
  highlightNames?: string[]
  visiblePlanes?: string[]
}

// Component to render a loaded GLB model
function ThreeDModel({ url, highlightNames, visiblePlanes }: ThreeDModelProps): React.JSX.Element | null {
  //const [clonedScene, setClonedScene] = React.useState<THREE.Group<THREE.Object3DEventMap>>();
  const { scene } = useGLTF(url);

  const nonHighlightedColor = new THREE.Color('#686868');

  const clonedScene = useMemo(() => {
    const cloned = scene.clone(true);
    cloned.traverse((obj: any) => {
      if (!obj.isMesh || !obj.material) return;
      const mats = Array.isArray(obj.material) ? obj.material : [obj.material];
      const newMats = mats.map((m: any) => {
        // … any static tweaks …
        // Ensure texture color space
        if (m.map) {
          m.map.colorSpace = THREE.SRGBColorSpace;
          m.map.needsUpdate = true;
        }
        // Avoid ultra-flat or ultra-gloss extremes that kill readable detail
        if (m.roughness !== undefined) {
          m.roughness = THREE.MathUtils.clamp(m.roughness, 0.25, 0.85);
        }
        if (m.metalness !== undefined) {
          m.metalness = THREE.MathUtils.clamp(m.metalness, 0.0, 1.0);
        }

        // make a per-mesh copy once
        const unique = m.clone();
        const pbr = unique as any;

        if (pbr.color && !unique.userData.originalColor) {
          // cache original color ON the material instance
          unique.userData.originalColor = pbr.color.clone();
        }
        return unique;
      });
      obj.material = Array.isArray(obj.material) ? newMats : newMats[0];
    });
    return cloned;
  }, [scene]);

  React.useEffect(() => {
    clonedScene.traverse((obj: any) => {
      if (obj.isMesh && obj.material) {
        const mats = Array.isArray(obj.material) ? obj.material : [obj.material];
        const isPlane = obj.name.includes('plane');
        const isVertebraeHighlighted = highlightNames == null || highlightNames.includes('All') || highlightNames.includes(obj.name);
        const isPlaneVisible = visiblePlanes == null || visiblePlanes.includes('All') || visiblePlanes.includes(obj.name);

        mats.forEach((mat: any) => {
          if (!mat?.color) return;

          if (isPlane) {
            obj.visible = isPlaneVisible;
          } else {
            if (isVertebraeHighlighted) {
              const orig: THREE.Color | undefined = mat.userData.originalColor;
              if (orig) mat.color.copy(orig);
            } else {
              mat.color.set(nonHighlightedColor);
            }
          }

        });
      }
    });
  }, [highlightNames, clonedScene]
  );
  return clonedScene ? <primitive object={clonedScene} /> : null;
}
export default ThreeDModel;
